import { fromCSS } from '@bacons/css-to-expo-linear-gradient'
import type { LinearGradientProps } from 'expo-linear-gradient'
import { Redirect } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage, type MessageDescriptor } from 'react-intl'
import { ScrollView, View } from 'react-native'

import { BasicHeader } from '@/src/elements/header/BasicHeader'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { useTagQuery } from '@/src/features/tag/services/useTagQuery'
import { ResponsiveDisplay } from '@/src/general/components/ResponsiveDisplay'
import { TEST_ID } from '@/src/general/constants/testId'
import { HTTPException } from '@/src/general/errors/HTTPException'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import { COMMUNITIES_CATEGORY, EVENT_ORIGIN } from '../../../models/community.constants'
import { getCommunitiesCategoryQueryParams, useCommunitiesQuery } from '../../../services/useCommunitiesQuery'
import { CommunitiesCategoryTagImage } from '../../components/CommunitiesCategory/CommunitiesCategoryTagImage'
import { CommunitiesGrid } from '../../components/CommunitiesGrid'
import { CommunitiesLoadMoreButton } from '../../components/CommunitiesLoadMoreButton'
import { CommunitiesCategoryMobile } from './CommunitiesCategoryMobile'
import { CommunitiesCategoryStaticImage } from './CommunitiesCategoryStaticImage'

const hasAtLeastTwo = <T,>(array: readonly T[]): array is readonly [T, T, ...T[]] => array.length >= 2

const makeLinearGradientProps = (
  cssGradient: string,
  fallbackColors: [string, string],
  fallbackLocations: [number, number] = [0, 1],
): LinearGradientProps => {
  const gradient = fromCSS(cssGradient)
  const colors = hasAtLeastTwo(gradient.colors) ? gradient.colors : fallbackColors
  const locations = hasAtLeastTwo(gradient.locations) ? gradient.locations : fallbackLocations
  return {
    ...gradient,
    colors,
    locations,
  }
}

const categoriesInfo: Record<
  Exclude<COMMUNITIES_CATEGORY, COMMUNITIES_CATEGORY.TAG>,
  {
    imageSource: string
    linearGradientProps: LinearGradientProps
    name: MessageDescriptor['id']
    textColor: string
    origin: EVENT_ORIGIN
    source: TRACKING_SOURCE
  }
> = {
  [COMMUNITIES_CATEGORY.SUGGESTED]: {
    imageSource: require('@/img/communities/suggested-for-you.png'),
    linearGradientProps: makeLinearGradientProps('linear-gradient(228deg, #FFF0C7 0%, #FFDAB9 100%);', [
      '#FFF0C7',
      '#FFDAB9',
    ]),
    name: 'community.communities.suggested',
    textColor: 'text-primary-text-contrast-high',
    origin: EVENT_ORIGIN.SUGGESTED_PAGE,
    source: TRACKING_SOURCE.SUGGESTED_PAGE,
  },
  [COMMUNITIES_CATEGORY.CURRENTLY]: {
    imageSource: require('@/img/communities/currently.png'),
    linearGradientProps: makeLinearGradientProps('linear-gradient(85deg, #D6E4FF 0%, #CBEFFF 100%);', [
      '#D6E4FF',
      '#CBEFFF',
    ]),
    name: 'words.ongoing',
    textColor: 'text-secondary-text-contrast-high',
    origin: EVENT_ORIGIN.CURRENTLY_PAGE,
    source: TRACKING_SOURCE.CURRENTLY_PAGE,
  },
  [COMMUNITIES_CATEGORY.TEAM_FAVORITES]: {
    imageSource: require('@/img/communities/team-favorites.png'),
    linearGradientProps: makeLinearGradientProps('linear-gradient(228deg, #FFF0C7 0%, #FFDAB9 100%);', [
      '#FFF0C7',
      '#FFDAB9',
    ]),
    name: 'community.communities.team-favorite',
    textColor: 'text-danger-text-contrast-high',
    origin: EVENT_ORIGIN.TEAM_FAVORITES_PAGE,
    source: TRACKING_SOURCE.TEAM_FAVORITES_PAGE,
  },
}

const isStaticCategoryTag = (tag?: string): tag is keyof typeof categoriesInfo => !!tag && tag in categoriesInfo

type TCommunitiesCategoryScreenProps = { tag: string }

export const CommunitiesCategoryScreen = ({ tag }: TCommunitiesCategoryScreenProps): ReactNode => {
  const { routes } = useRoutes()

  const communityQuery = useCommunitiesQuery(getCommunitiesCategoryQueryParams(tag || ''))

  const { error } = useTagQuery(tag || '', !isStaticCategoryTag(tag))

  if (error) {
    if (error instanceof HTTPException && error.status === 404) {
      return <Redirect href={routes.communities()} />
    }
    return <ErrorScreen error={error} defaultErrorKey="community.load.error" />
  }

  const getCommunitiesCategoryTagImageProps = () => {
    if (isStaticCategoryTag(tag)) {
      const { imageSource, linearGradientProps, name, textColor } = categoriesInfo[tag]
      return {
        ImageComponent: () => <CommunitiesCategoryStaticImage {...linearGradientProps} imageSource={imageSource} />,
        customTagName: {
          text: <FormattedMessage id={name} />,
          className: textColor,
        },
      }
    }
    return { tag }
  }

  const sourceAndOrigin = isStaticCategoryTag(tag)
    ? { origin: categoriesInfo[tag].origin, source: categoriesInfo[tag].source }
    : { origin: EVENT_ORIGIN.TAG_PAGE, source: TRACKING_SOURCE.TAG_PAGE }

  return (
    <View testID={TEST_ID.COMMUNITIES.CATEGORY_SCREEN} className="flex-1">
      <ResponsiveDisplay size="xs" behavior="maxWidth">
        <CommunitiesCategoryMobile
          {...communityQuery}
          {...getCommunitiesCategoryTagImageProps()}
          {...sourceAndOrigin}
        />
      </ResponsiveDisplay>
      <ResponsiveDisplay size="sm" behavior="minWidth">
        <ScrollView className="w-full">
          <View className="z-10">
            <BasicHeader goBackTo={routes.communities()} />
          </View>
          <CommunitiesCategoryTagImage {...getCommunitiesCategoryTagImageProps()} />
          <View className="mt-[-16px] rounded-se-md rounded-ss-md pt-2">
            <CommunitiesGrid centered {...communityQuery} {...sourceAndOrigin} />
          </View>
          <CommunitiesLoadMoreButton {...communityQuery} />
          <Spacer y={20} />
        </ScrollView>
      </ResponsiveDisplay>
    </View>
  )
}
